// Single source of truth for image URL → CDN-variant mapping.
//
// The backend (operator-api files module) pre-generates a ladder of immutable,
// edge-cached WebP variants per uploaded image and stores them on the R2 CDN
// at `<base>.<variant>.webp`:
//
//   small 480px · medium 800px · large 1280px · trip 1920px (WebP q75)
//
// URLs here always end `.webp` — WebP is the only format the backend encodes,
// so the same-origin `/media` route serves the requested key verbatim with no
// `Accept` negotiation (see `app/media/[...key]/route.ts`).
//
// `ImageDto.url` already points at the `large` variant. Routing these through
// the self-hosted Next.js image optimizer (`/_next/image`) re-fetches and
// re-encodes an already-optimal WebP — adding cold-start latency and bypassing
// the global CDN edge cache. Instead we serve the right-sized variant straight
// from the edge (see `cdnImageLoader`, wired as `images.loaderFile`).
//
// This module is imported by BOTH the next/image loader AND the gallery
// prefetch code, so the URL a slot prefetches is byte-identical to the URL
// next/image will request — the prefetch warms exactly the right cache entry.

const VARIANT_ORDER = [
  { name: "small", width: 480 },
  { name: "medium", width: 800 },
  { name: "large", width: 1280 },
  { name: "trip", width: 1920 },
] as const;

export const SMALL_WIDTH = 480;
export const LARGE_WIDTH = 1280;

const VARIANT_SUFFIX_RE = /^(.*)\.(small|medium|large|trip)\.webp$/;

/** Strip a `.small|medium|large|trip.webp` suffix to the base storage URL, or
 *  null when `url` is not one of our pre-generated CDN variants. */
export function variantBase(url: string): string | null {
  const m = VARIANT_SUFFIX_RE.exec(url);
  return m ? (m[1] ?? null) : null;
}

export function isCdnVariant(url: string): boolean {
  return VARIANT_SUFFIX_RE.test(url);
}

/** Pick the smallest pre-generated variant whose width covers `width`. */
function pickVariant(width: number): (typeof VARIANT_ORDER)[number]["name"] {
  for (const v of VARIANT_ORDER) if (v.width >= width) return v.name;
  return "trip";
}

/** Is this an Unsplash CDN photo? Unsplash resizes on the fly via query
 *  params, so it can participate in the same responsive ladders as our own
 *  pre-generated CDN variants. */
export function isUnsplashUrl(url: string): boolean {
  // Hostname check, not substring: `example.com/images.unsplash.com.jpg`
  // must not be treated as an Unsplash photo (it would get resize params
  // appended that the origin doesn't understand).
  try {
    return new URL(url).hostname === "images.unsplash.com";
  } catch {
    return false;
  }
}

/** Build one Unsplash rung: width (+ optional cover-crop height) at a fixed
 *  quality. Returns null when `url` doesn't parse. */
function unsplashRung(
  url: string,
  width: number,
  quality: number,
  height?: number,
): string | null {
  try {
    const u = new URL(url);
    u.searchParams.set("w", String(width));
    if (height !== undefined) u.searchParams.set("h", String(height));
    else u.searchParams.delete("h");
    u.searchParams.set("q", String(quality));
    u.searchParams.set("auto", "format");
    u.searchParams.set("fit", "crop");
    return u.toString();
  } catch {
    return null;
  }
}

/** Map any image URL to the URL that should actually be fetched at `width`.
 *  - CDN variant URLs → the nearest pre-generated variant (edge-cached).
 *  - Unsplash → its own on-the-fly resize API.
 *  - Anything else → unchanged (served as-is, no Next optimizer round-trip). */
export function variantUrlForWidth(
  url: string,
  width: number,
  quality = 75,
): string {
  const base = variantBase(url);
  if (base) return `${base}.${pickVariant(width)}.webp`;

  if (isUnsplashUrl(url)) {
    return unsplashRung(url, width, quality) ?? url;
  }

  return url;
}

/** next/image custom loader (wired globally via `images.loaderFile`). Replaces
 *  the built-in `/_next/image` optimizer so already-optimized, edge-cached
 *  assets are served directly from the CDN. next/image still builds the
 *  responsive `srcset` from `deviceSizes`/`imageSizes` and calls this per
 *  width, so we keep responsive delivery — minus the redundant re-encode. */
export default function cdnImageLoader({
  src,
  width,
  quality,
}: {
  src: string;
  width: number;
  quality?: number;
}): string {
  return variantUrlForWidth(src, width, quality ?? 75);
}

/** Normalize any media URL to the same-origin `/media/agencies/…` proxy path.
 *  Raw CDN hosts expose `/agencies/…`; tenant-proxy URLs already carry
 *  `/media/agencies/…`. Same-origin fetches ride the same-origin `/media`
 *  route, need no cross-origin handshake, and keep srcset entries short in
 *  the ISR HTML. Non-media URLs (external, data:, already-relative) pass
 *  through untouched. */
export function toSameOriginMediaPath(url: string): string {
  if (!url) return url;
  try {
    const p = new URL(url).pathname;
    if (p.startsWith("/agencies/")) return `/media${p}`;
    if (p.startsWith("/media/agencies/")) return p;
    return url;
  } catch {
    return url;
  }
}
